import {Injectable} from '@angular/core';
import {ModalController} from '@ionic/angular';
import {GlobalService} from './global.service';
import {UserIdleService} from 'angular-user-idle';
import {CountdownModalComponent} from '../components/countdown-modal/countdown-modal.component';
import {Subject} from 'rxjs';

/**
 * Service for checking screen idleness and handling session timeout.
 */
@Injectable({providedIn: 'root'})
export class UserIdleTimerService {
    constructor(
        private userIdle: UserIdleService,
        private global: GlobalService,
        private modalController: ModalController
    ) {}

    /** Indicates whether the countdown modal is currently shown. */
    isTimer?: boolean;

    /** Holds the current timer count. */
    timerCount = 0;

    /** Emits the timer count updates. */
    private timerCountSubject = new Subject<number>();

    /** Observable for timer count to be used by modal component. */
    getTimerCountObservable() {
        return this.timerCountSubject.asObservable();
    }

    /**
     * Starts monitoring user activity. Shows a countdown modal if idle.
     * Logs out the user when idle timeout is reached.
     */
    startWatching() {
        this.userIdle.startWatching();
        this.global.isWatching = true;

        this.userIdle.onTimerStart().subscribe((count) => {
            if (!this.global.loggedInUser) {
                this.stop();
                this.stopWatching();
                return;
            }

            this.timerCount = count;
            this.timerCountSubject.next(count);

            // If only one browser tab is active, show the countdown modal
            if (+localStorage.getItem('tabs') === 1) {
                if (!this.isTimer) {
                    this.isTimer = true;
                    this.global.countFinished = false;
                    this.openCountdownModal();
                }
            } else {
                this.resetTimer();
            }
        });

        this.userIdle.onTimeout().subscribe(() => {
            this.global.logoutUser();
        });
    }

    /** Stops the timer and performs cleanup. */
    stop() {
        this.userIdle.stopTimer();
        this.finalizeTimerCleanup();
    }

    /** Stops watching user activity and performs cleanup. */
    stopWatching() {
        this.userIdle.stopWatching();
        this.finalizeTimerCleanup();
    }

    /** Resets the idle timer and performs cleanup. */
    resetTimer() {
        this.userIdle.resetTimer();
        this.finalizeTimerCleanup();
    }

    /**
     * Opens the countdown modal using Ionic's ModalController.
     */
    private async openCountdownModal() {
        const timeout = this.userIdle.getConfigValue().timeout;

        const countdownModal = await this.modalController.create({
            component: CountdownModalComponent,
            componentProps: {
                initialCount: timeout - this.timerCount,
                timerCountObservable: this.getTimerCountObservable(),
            },
            backdropDismiss: false,
            cssClass: 'auto-height-width',
        });

        await countdownModal.present();

        countdownModal.onDidDismiss().then(() => {
            this.resetTimer();
        });
    }

    /**
     * Handles common cleanup tasks when stopping/resetting idle timer.
     */
    private finalizeTimerCleanup() {
        this.global.isWatching = false;
        this.cleanupTimer();
    }

    /** Resets the isTimer flag. */
    private cleanupTimer() {
        this.isTimer = false;
    }
}
